import { computed, onMounted, onUnmounted } from 'vue'
import { useLocalStorage, useThrottleFn } from '@vueuse/core'

export const SOUND_TYPE = {
  UPDATE: 'UPDATE',
  ERROR: 'ERROR',
  COMPLETE: 'COMPLETE',
  DO_IT: 'DO_IT',
  NOICE: 'NOICE',
  SCAN: 'SCAN'
}

const soundSources = {
  [SOUND_TYPE.UPDATE]: '/audio/notification.mp3',
  [SOUND_TYPE.ERROR]: '/audio/error.mp3',
  [SOUND_TYPE.COMPLETE]: '/audio/complete.mp3',
  [SOUND_TYPE.DO_IT]: '/audio/do-it.mp3',
  [SOUND_TYPE.NOICE]: '/audio/noice.mp3',
  [SOUND_TYPE.SCAN]: '/audio/scan.mp3'
}

const muted = useLocalStorage('notifications_muted', false)

export const useSound = (sounds = []) => {
  if (!Array.isArray(sounds)) {
    sounds = [sounds]
  }

  let soundLibrary = {}
  let abortControllers = {}

  const clearAudio = (audio) => {
    audio.src = ''
    audio.load()
  }

  const loadSound = (sound) => {
    abortControllers[sound] = new AbortController()
    const { signal } = abortControllers[sound]

    return new Promise((resolve, reject) => {
      const audio = new Audio()

      audio.addEventListener(
        'canplaythrough',
        () => {
          soundLibrary[sound] = audio
          resolve(audio)
        },
        { signal }
      )

      audio.addEventListener(
        'error',
        () => {
          reject(new Error('Failed to load sound'))
        },
        { signal }
      )

      audio.src = soundSources[sound]
      audio.load()

      signal.addEventListener('abort', () => {
        clearAudio(audio)
        reject(new Error('Sound loading aborted'))
      })
    })
  }

  onMounted(() => {
    Promise.all(sounds.map((sound) => loadSound(sound))).catch((error) => console.error('Error loading sounds:', error))
  })

  onUnmounted(() => {
    Object.values(abortControllers).forEach((controller) => controller.abort())
    Object.values(soundLibrary).forEach((audio) => clearAudio(audio))

    soundLibrary = null
    abortControllers = null
  })

  const playSound = useThrottleFn((sound) => {
    if (muted.value) {
      return
    }

    if (!soundLibrary[sound]) {
      console.warn('Sound not loaded', sound)
      return
    }

    soundLibrary[sound].play()
  }, 500)

  const enableSound = () => {
    muted.value = false
  }

  const disableSound = () => {
    muted.value = true
  }

  const isSoundMuted = computed(() => muted.value)

  return {
    playSound,

    isSoundMuted,
    enableSound,
    disableSound
  }
}
